"use client";

import { useEffect, useId, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

type FrequencyOption = { value: string; label: string };

type Props = {
  /** Element id for the trigger — the associated <label htmlFor> points here. */
  id: string;
  /** Id of the visible field label, wired via aria-labelledby. */
  labelId: string;
  value: string;
  options: FrequencyOption[];
  onChange: (value: string) => void;
  /** Fired on first meaningful interaction (open or select) for analytics. */
  onInteract?: () => void;
};

/**
 * Accessible listbox dropdown that replaces a native <select>.
 *
 * A native <select>'s *open* menu is drawn by the OS/browser and can't be
 * themed or repositioned with CSS — on macOS/Safari (and some embedded
 * browsers) it renders as a light system menu that clashes with the dark UI.
 * This component renders the open list as real, theme-token-styled DOM so it
 * matches light/dark everywhere. Keyboard + ARIA follow the WAI-ARIA listbox
 * pattern (roving `aria-activedescendant`, not focus per option).
 */
export default function FrequencySelect({
  id,
  labelId,
  value,
  options,
  onChange,
  onInteract,
}: Props) {
  const [open, setOpen] = useState(false);
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );
  const [activeIndex, setActiveIndex] = useState(selectedIndex);
  // Open direction + height cap, decided at open time from the room around the
  // trigger so the list never runs off-screen when the field sits low in the
  // viewport (it flips above and caps its height to the available space).
  const [placement, setPlacement] = useState<"bottom" | "top">("bottom");
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const baseId = useId();
  const optionDomId = (index: number) => `${baseId}-option-${index}`;
  const selected = options[selectedIndex];

  // Close when a pointer lands outside the widget.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  // Move focus into the list when it opens so arrow keys drive it immediately.
  useEffect(() => {
    if (open) {
      listRef.current?.focus();
    }
  }, [open]);

  // Keep the highlighted option scrolled into view during keyboard nav.
  useEffect(() => {
    if (!open) {
      return;
    }
    document
      .getElementById(`${baseId}-option-${activeIndex}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex, baseId]);

  const openMenu = () => {
    onInteract?.();
    setActiveIndex(selectedIndex);
    const trigger = buttonRef.current;
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      const margin = 16;
      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;
      // Rough list height (row + gap, capped like the scroll ceiling) is enough
      // to pick a side; the maxHeight below keeps it on-screen either way.
      const estimated = Math.min(options.length, 6) * 44 + 8;
      const openUp = spaceBelow < estimated && spaceAbove > spaceBelow;
      setPlacement(openUp ? "top" : "bottom");
      setMaxHeight(Math.max(120, Math.floor(openUp ? spaceAbove : spaceBelow)));
    }
    setOpen(true);
  };

  const closeMenu = (focusButton = true) => {
    setOpen(false);
    if (focusButton) {
      buttonRef.current?.focus();
    }
  };

  const commit = (index: number) => {
    const option = options[index];
    if (option) {
      onChange(option.value);
      onInteract?.();
    }
    closeMenu();
  };

  const onButtonKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
      event.preventDefault();
      openMenu();
    }
  };

  const onListKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((index) => Math.min(options.length - 1, index + 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((index) => Math.max(0, index - 1));
        break;
      case "Home":
        event.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        event.preventDefault();
        setActiveIndex(options.length - 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        commit(activeIndex);
        break;
      case "Escape":
        event.preventDefault();
        closeMenu();
        break;
      case "Tab":
        closeMenu(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${labelId} ${id}`}
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={onButtonKeyDown}
        className="flex w-full items-center justify-between gap-2 rounded-xl border border-input bg-background px-4 py-3 text-left text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <span className="truncate">{selected?.label}</span>
        <ChevronDown
          aria-hidden="true"
          className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <ul
          ref={listRef}
          role="listbox"
          tabIndex={-1}
          aria-labelledby={labelId}
          aria-activedescendant={optionDomId(activeIndex)}
          onKeyDown={onListKeyDown}
          style={maxHeight ? { maxHeight } : undefined}
          className={`absolute left-0 z-50 max-h-64 w-full overflow-auto rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-lg focus:outline-none ${
            placement === "top" ? "bottom-full mb-2" : "top-full mt-2"
          }`}
        >
          {options.map((option, index) => {
            const isSelected = option.value === value;
            const isActive = index === activeIndex;
            return (
              <li
                key={option.value}
                id={optionDomId(index)}
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => commit(index)}
                className={`flex cursor-pointer items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm ${
                  isActive
                    ? "bg-accent text-accent-foreground"
                    : "text-popover-foreground"
                }`}
              >
                <span>{option.label}</span>
                {isSelected && (
                  <Check
                    aria-hidden="true"
                    className="h-4 w-4 shrink-0 text-primary"
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
